/**
 * Device Transfer Service
 * Converts profiles to MIDI messages and sends to Looper Plus
 */

import { getMIDICommand } from '../constants/functions.js';

/**
 * Build an ordered, PURE list of MIDI command descriptors for a profile.
 *
 * Iterates the profile's ENABLED footswitches (fs1, fs2, ext1, ext2) followed
 * by the encoder, resolving each assigned function via getMIDICommand().
 * No side effects -- nothing is transmitted here, so this is safe to unit test.
 *
 * @param {object} profile - a profile object (see useStore default shape)
 * @returns {Array<{source:string,label:string,type:('note'|'cc'|'combo'),value?:number,values?:number[],channel:number}>}
 *
 * Example:
 *   buildProfileCommands({
 *     footswitches: {
 *       fs1: { function: 'rec_play', enabled: true },
 *       fs2: { function: 'stop_all', enabled: true },
 *       ext1: { function: 'undo_redo', enabled: false },
 *       ext2: { function: 'clear', enabled: false }
 *     },
 *     encoder: { function: 'volume' },
 *     midiSettings: { channel: 2 }
 *   })
 *   // => [
 *   //   { source: 'fs1', label: 'rec_play', type: 'note',  value: 11,        channel: 2 },
 *   //   { source: 'fs2', label: 'stop_all', type: 'combo', values: [7, 8],   channel: 2 },
 *   //   { source: 'encoder', label: 'volume', type: 'cc',  value: 7,         channel: 2 }
 *   // ]
 */
export function buildProfileCommands(profile) {
    const commands = [];

    if (!profile) return commands;

    const channel =
        profile.midiSettings && profile.midiSettings.channel && profile.midiSettings.channel !== 'omni'
            ? profile.midiSettings.channel
            : 1;

    const footswitches = profile.footswitches || {};
    const order = ['fs1', 'fs2', 'ext1', 'ext2'];

    for (const source of order) {
        const fs = footswitches[source];
        if (!fs || !fs.enabled) continue;

        const midiCmd = getMIDICommand(fs.function);
        if (!midiCmd) continue;

        const descriptor = { source, label: fs.function, type: midiCmd.type, channel };
        if (midiCmd.type === 'combo') {
            descriptor.values = midiCmd.values;
        } else {
            descriptor.value = midiCmd.value;
        }
        commands.push(descriptor);
    }

    // Encoder (typically a CC, but resolved generically)
    if (profile.encoder && profile.encoder.function) {
        const encoderCmd = getMIDICommand(profile.encoder.function);
        if (encoderCmd) {
            const descriptor = {
                source: 'encoder',
                label: profile.encoder.function,
                type: encoderCmd.type,
                channel
            };
            if (encoderCmd.type === 'combo') {
                descriptor.values = encoderCmd.values;
            } else {
                descriptor.value = encoderCmd.value;
            }
            commands.push(descriptor);
        }
    }

    return commands;
}

class DeviceTransferService {
    constructor(midiService) {
        this.midiService = midiService;
    }

    /**
     * Read configuration from device
     * Note: Looper Plus doesn't support reading config via MIDI
     */
    async readFromDevice() {
        return {
            success: false,
            error: 'Looper Plus does not support reading configuration via MIDI. You can manually enter your device settings.',
            note: 'To import settings: Go to device menu, note your assignments, then recreate in this app.'
         };
     }
 }

export default DeviceTransferService;
